import React, { useEffect, useRef, useState } from 'react';
import './ScratchCoin.css';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../App';
import { useFireAuth } from '../FireAuthContext';
import useBank from './useBank';
import useScratchCanvas from '../utils/useScratchCanvas';

const coinSymbol = '🪙';
const nonWinningSymbols = ['🍌', '🍒', '🍇', '🍉', '🍍', '🍓', '🍑', '🍋'];
const probability = 0.5;

const ScratchCoin = ({ refreshUser }) => {
  const { user } = useFireAuth();
  const canvasRef = useRef(null);
  const [isScratching, setIsScratching] = useState(false);
  const [element, setElement] = useState(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [message, setMessage] = useState('');
  const { withdrawHahumCoin } = useBank();
  const isProcessing = useRef(false);
  const { initCanvasBackground, getEventCoordinates, drawScratch } = useScratchCanvas();

  useEffect(() => {
    const initializeCanvas = (canvas) => {
      initCanvasBackground(canvas, { willReadFrequently: true });

      const randomValue = Math.random();
      const symbol = randomValue < probability ? coinSymbol : nonWinningSymbols[Math.floor(Math.random() * nonWinningSymbols.length)];

      setElement({
        x: 0,
        y: 0,
        width: canvas.width,
        height: canvas.height,
        symbol,
      });
    };

    initializeCanvas(canvasRef.current);
  }, [initCanvasBackground]);

  const handleScratch = (e) => {
    if (gameEnded) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const { x, y } = getEventCoordinates(e, canvas);
    drawScratch(canvas, x, y);

    const scratchedPixels = context.getImageData(0, 0, canvas.width, canvas.height).data.reduce((count, value, index) => {
      if ((index + 1) % 4 === 0 && value === 0) {
        count++;
      }
      return count;
    }, 0);

    const totalPixels = canvas.width * canvas.height;
    const scratchPercentage = (scratchedPixels / totalPixels) * 100;

    if (scratchPercentage > 50 && !gameEnded) {
      setGameEnded(true);
      setTimeout(async () => {
        if (isProcessing.current) return;
        isProcessing.current = true;

        try {
          if (element.symbol === coinSymbol) {
            await withdrawHahumCoin(2);
            setMessage(`You Win 🪙2!`);
          } else {
            setMessage('Not a winner this time 🫤');
          }
          await incrementGamesPlayed();
          await refreshUser();
        } catch (error) {
          console.error('Error processing scratch coin result:', error);
          setMessage('Error processing result. Please try again.');
        } finally {
          isProcessing.current = false;
        }
      }, 500);
    }
  };

  const incrementGamesPlayed = async () => {
    if (user) {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        await updateDoc(userDocRef, {
          gamesPlayed: increment(1),
        });
      } catch (error) {
        console.error('Error incrementing games played:', error);
      }
    }
  };

  return (
    <div className="scratch-coin">
      <div className="scratch-coin-rules">
        <p>Scratch to reveal the symbol. Reveal a 🪙 to win 2 coins!</p>
      </div>
      <div className="scratch-coin-window">
        <canvas
          ref={canvasRef}
          width={320}
          height={320}
          className={`scratch-coin-canvas ${gameEnded ? 'fade-out' : ''}`}
          onMouseDown={() => setIsScratching(true)}
          onMouseUp={() => setIsScratching(false)}
          onMouseMove={(e) => isScratching && handleScratch(e)}
          onTouchStart={() => setIsScratching(true)}
          onTouchEnd={() => setIsScratching(false)}
          onTouchMove={(e) => isScratching && handleScratch(e)}
        />
        {element && <div className="scratch-coin-symbol">{element.symbol}</div>}
      </div>
      <div className="scratch-coin-info">
        <p>{message}</p>
      </div>
    </div>
  );
};

export default ScratchCoin;
